<!--
  LaunchStatusLists.vue -- the two labelled lists of the Review & Launch
  status block (Trello #554).

    * "Must fix to launch": the server's launch reasons. The headline and
      the Launch button above it follow this list and nothing else.
    * "Needs attention on this live store": the same kind of rows on a store
      that is already live (nothing to launch, so nothing "must" be fixed).
    * "Recommended": guidance that does not stop a launch.

  Rows are built by launchStatusRows.js; this component only draws them and
  emits the row's fix target (``fix``) for the parent to follow.
-->
<template>
  <div class="lsl">
    <section
      v-if="mustFix.length"
      class="lsl__group lsl__group--must"
      data-testid="review-launch-must-fix"
    >
      <h3 class="lsl__h">
        <span class="tf-icon" aria-hidden="true">error_outline</span>Must fix to launch
      </h3>
      <ul class="lsl__list">
        <li
          v-for="row in mustFix"
          :key="row.key"
          class="lsl__row"
          :data-testid="`review-launch-must-fix-${row.key}`"
        >
          <span
            class="lsl__text"
            :data-testid="`review-launch-must-fix-${row.key}-text`"
          >{{ row.text }}</span>
          <a
            v-if="row.fix"
            class="lsl__link"
            href="#"
            :data-testid="`review-launch-must-fix-${row.key}-link`"
            @click.prevent="$emit('fix', row.fix)"
          >{{ row.fix.label }} &rarr;</a>
        </li>
      </ul>
    </section>

    <section
      v-else-if="attention.length"
      class="lsl__group lsl__group--must"
      data-testid="review-launch-live-attention"
    >
      <h3 class="lsl__h">
        <span class="tf-icon" aria-hidden="true">error_outline</span>Needs attention on this live store
      </h3>
      <ul class="lsl__list">
        <li
          v-for="row in attention"
          :key="row.key"
          class="lsl__row"
          :data-testid="`review-launch-live-attention-${row.key}`"
        >
          <span class="lsl__text">{{ row.text }}</span>
          <a
            v-if="row.fix"
            class="lsl__link"
            href="#"
            :data-testid="`review-launch-live-attention-${row.key}-link`"
            @click.prevent="$emit('fix', row.fix)"
          >{{ row.fix.label }} &rarr;</a>
        </li>
      </ul>
    </section>

    <section
      v-if="recommended.length"
      class="lsl__group"
      data-testid="review-launch-recommended"
    >
      <h3 class="lsl__h">
        <span class="tf-icon" aria-hidden="true">lightbulb</span>Recommended
      </h3>
      <p class="lsl__note">These do not stop this store from launching.</p>
      <ul class="lsl__list">
        <li
          v-for="row in recommended"
          :key="row.key"
          class="lsl__row"
          :class="{ 'lsl__row--emphasis': row.emphasis }"
          :data-testid="`review-launch-recommended-${row.key}`"
        >
          <span class="lsl__text">{{ row.text }}</span>
          <a
            v-if="row.fix"
            class="lsl__link"
            href="#"
            :data-testid="`review-launch-recommended-${row.key}-link`"
            @click.prevent="$emit('fix', row.fix)"
          >{{ row.fix.label }} &rarr;</a>
        </li>
      </ul>
    </section>

    <p
      v-if="loading"
      class="lsl__loading"
      data-testid="review-launch-readiness-loading"
    >Checking readiness...</p>
  </div>
</template>

<script>
export default {
  name: "LaunchStatusLists",
  props: {
    mustFix: { type: Array, default: () => [] },
    attention: { type: Array, default: () => [] },
    recommended: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  emits: ["fix"],
};
</script>

<style scoped>
.lsl { display: flex; flex-direction: column; gap: 18px; }
.lsl__group { display: flex; flex-direction: column; gap: 8px; }
.lsl__h {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font: 500 16px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-secondary);
}
.lsl__group--must .lsl__h .tf-icon { color: var(--tf-error-dark, #9c1b1b); font-size: 20px; }
.lsl__group:not(.lsl__group--must) .lsl__h .tf-icon { color: var(--tf-warning-fg); font-size: 20px; }
.lsl__note {
  margin: 0;
  font: 400 13px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-fg-4);
}
.lsl__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lsl__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--tf-border);
  font: 400 14px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-fg-1);
  line-height: 1.5;
}
.lsl__group--must .lsl__row {
  border-color: rgba(225, 50, 50, 0.35);
  background: var(--tf-error-soft-alt, #fdf1f1);
}
.lsl__row--emphasis {
  border-color: rgba(255, 168, 0, 0.55);
  background: rgba(255, 168, 0, 0.07);
}
.lsl__text { flex: 1 1 320px; }
.lsl__row--emphasis .lsl__text { font-weight: 500; }
.lsl__link {
  color: var(--tf-primary);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
}
.lsl__link:hover { text-decoration: underline; }
.lsl__loading {
  margin: 0;
  font: 400 13px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-fg-4);
}
</style>
